/* User Management — list page. Search, role/status filters, pagination,
   inline status toggle, row actions and the create/edit drawer. */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import s from "./userManagement.module.css";
import {
  IoAddOutline,
  IoPencil,
  IoTrashOutline,
  IoKeyOutline,
  IoPeopleOutline,
  IoShieldCheckmarkOutline,
  IoMailOutline,
} from "react-icons/io5";
import {
  listUsers,
  deleteUser,
  setUserStatus,
  resetPassword,
  resendInvite,
} from "../../rbac/rbacService";
import { useRbac } from "../../rbac/RbacContext";
import { showToast } from "../../utils/Toastify/Toastify";
import { formatDate } from "../../utils/dateFormatter";
import {
  Avatar,
  StatusBadge,
  RoleChip,
  Spinner,
  EmptyState,
  SearchInput,
  Pagination,
  Toggle,
  ConfirmDialog,
} from "./uiBits";
import UserFormModal from "./UserFormModal";

const PAGE_SIZE = 8;

const UsersList = () => {
  const navigate = useNavigate();
  const { roles, refreshRoles } = useRbac();

  // Refresh the shared roles cache on mount so the create/edit drawer always
  // offers the current set of roles (not a stale copy from initial app load).
  useEffect(() => {
    refreshRoles();
  }, [refreshRoles]);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type, user }
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null); // row currently resending

  const rolesById = roles.reduce((acc, r) => ((acc[r.id] = r), acc), {});

  // Only staff-assignable roles appear in the filter — this screen lists staff
  // only, so filtering by the Creator/Subscriber audience roles would always be
  // empty. (Admin + custom roles are kept.)
  const staffRoles = roles.filter(
    (r) => !(r.system && (r.name === "Creator" || r.name === "Subscriber"))
  );

  // A user assigned the Admin system role is protected — the UI hides
  // deactivate/delete for them (the backend also blocks removing the last admin).
  const isProtectedAdmin = (user) =>
    (user.roleIds || []).some((rid) => {
      const role = rolesById[rid];
      return role && role.system && role.name === "Admin";
    });

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers({
        search: debounced,
        role: roleFilter,
        status: statusFilter,
        page,
        pageSize: PAGE_SIZE,
      });
      setUsers(res.data);
      setTotal(res.total);
    } catch {
      showToast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, [debounced, roleFilter, statusFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  // reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debounced, roleFilter, statusFilter]);

  const openCreate = () => {
    setEditingUser(null);
    setModalOpen(true);
  };
  const openEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleToggleStatus = async (user) => {
    const next = user.status === "active" ? "inactive" : "active";
    // optimistic
    setUsers((list) =>
      list.map((u) => (u.id === user.id ? { ...u, status: next } : u))
    );
    try {
      await setUserStatus(user.id, next);
      // "User deactivated" is exactly the account-level wording this feature
      // exists to avoid: for a granted row only CMS access changes, and an
      // operator switching off a paying subscriber would reasonably read it as
      // "I just disabled their subscription".
      showToast(
        user.isGranted
          ? next === "active"
            ? "CMS access restored"
            : "CMS access deactivated — their account is untouched"
          : next === "active"
          ? "User activated"
          : "User deactivated",
        "success"
      );
    } catch {
      showToast("Failed to update status", "error");
      load();
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      if (confirm.type === "delete") {
        await deleteUser(confirm.user.id);
        showToast(
          confirm.user.isGranted ? "CMS access revoked" : "User deleted",
          "success"
        );
        load();
      } else if (confirm.type === "reset") {
        await resetPassword(confirm.user.id);
        showToast("Password reset link sent", "success");
      }
      setConfirm(null);
    } catch (err) {
      showToast(err.message || "Action failed", "error");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleResend = async (user) => {
    if (resendingId) return; // guard against double-clicks across rows
    setResendingId(user.id);
    try {
      await resendInvite(user.id);
      showToast("Invitation resent", "success");
    } catch (err) {
      showToast(err.message || "Failed to resend invitation", "error");
    } finally {
      setResendingId(null);
    }
  };

  const activeCount = users.filter((u) => u.status === "active").length;

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div className={s.headerTitleWrap}>
          <h1 className={s.title}>User Management</h1>
          <p className={s.subtitle}>
            Manage accounts, assign roles and control access across the CMS.
          </p>
        </div>
        <div className={s.headerActions}>
          <button
            className={s.btnGhost}
            onClick={() => navigate("/mainlayout/roles")}
          >
            <IoShieldCheckmarkOutline /> Roles &amp; Permissions
          </button>
          <button className={s.btnPrimary} onClick={openCreate}>
            <IoAddOutline /> Invite user
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={s.stats}>
        <div className={s.statTile}>
          <span className={s.statValue}>{total}</span>
          <span className={s.statLabel}>Total users (filtered)</span>
        </div>
        <div className={s.statTile}>
          <span className={s.statValue}>{activeCount}</span>
          <span className={s.statLabel}>Active on this page</span>
        </div>
        <div className={s.statTile}>
          <span className={s.statValue}>{staffRoles.length}</span>
          <span className={s.statLabel}>Roles defined</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className={s.toolbar}>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or title…"
        />
        <select
          className={s.filterSelect}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All roles</option>
          {staffRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select
          className={s.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="invited">Invited</option>
        </select>
      </div>

      {/* Table */}
      <div className={s.tableCard}>
        {loading ? (
          <Spinner label="Loading users…" />
        ) : users.length === 0 ? (
          <EmptyState
            icon={<IoPeopleOutline />}
            title="No users found"
            message="Try adjusting your search or filters, or add a new user."
            action={
              <button className={s.btnPrimary} onClick={openCreate}>
                <IoAddOutline /> Add user
              </button>
            }
          />
        ) : (
          <>
            <div className={s.tableScroll}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Last active</th>
                    <th className={s.colActions}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div
                          className={s.userCell}
                          onClick={() =>
                            navigate(`/mainlayout/user-management/${user.id}`)
                          }
                        >
                          <Avatar name={user.name} color={user.avatarColor} />
                          <div className={s.userCellMeta}>
                            <span className={s.userName} title={user.name}>
                              {user.name}
                              {user.isGranted && (
                                <span
                                  className={s.grantedTag}
                                  title={`An existing ${user.accountType} account granted CMS access. Removing them revokes that access; their account is kept.`}
                                >
                                  {user.accountType === "creator" ? "Creator" : "Subscriber"}
                                </span>
                              )}
                            </span>
                            <span className={s.userEmail} title={user.email}>
                              {user.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={s.roleCellChips}>
                          {(user.roleIds || []).map((rid) =>
                            rolesById[rid] ? (
                              <RoleChip key={rid} role={rolesById[rid]} />
                            ) : null
                          )}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={user.status} />
                      </td>
                      <td>
                        <span className={s.userEmail}>
                          {user.lastActive
                            ? formatDate(user.lastActive)
                            : "Never"}
                        </span>
                      </td>
                      <td className={s.colActions}>
                        <div className={s.rowActions}>
                          {/* The activate/deactivate toggle only applies to users
                              who have ACCEPTED their invite. It's disabled for:
                              - Admins (protected; last-admin can't be deactivated)
                              - Invited users (they haven't set a password yet, so
                                activating them would strand the invite — you'd
                                lose the "Resend invite" button and they still
                                couldn't log in). Use Resend invite instead. */}
                          {isProtectedAdmin(user) || user.status === "invited" ? (
                            <Toggle
                              checked={user.status === "active"}
                              disabled
                              onChange={() => {}}
                            />
                          ) : (
                            <Toggle
                              checked={user.status === "active"}
                              // On a granted row this switches CMS access only
                              // — the control whose meaning most needs saying
                              // out loud, since the row also shows a website
                              // account that it does NOT affect.
                              title={
                                user.isGranted
                                  ? "Switch their CMS access on or off (their website account is unaffected)"
                                  : "Activate or deactivate this user"
                              }
                              onChange={() => handleToggleStatus(user)}
                            />
                          )}
                          <button
                            className={s.iconBtn}
                            title="Edit user details and role"
                            onClick={() => openEdit(user)}
                          >
                            <IoPencil />
                          </button>
                          {user.status === "invited" ? (
                            <button
                              className={s.iconBtn}
                              title={
                                resendingId === user.id
                                  ? "Sending…"
                                  : "Resend the invitation email"
                              }
                              disabled={resendingId === user.id}
                              onClick={() => handleResend(user)}
                            >
                              <IoMailOutline
                                className={
                                  resendingId === user.id ? s.spinIcon : undefined
                                }
                              />
                            </button>
                          ) : (
                            <button
                              className={s.iconBtn}
                              title="Email this user a link to set a new password"
                              onClick={() => setConfirm({ type: "reset", user })}
                            >
                              <IoKeyOutline />
                            </button>
                          )}
                          {!isProtectedAdmin(user) && (
                            <button
                              className={`${s.iconBtn} ${s.iconBtnDanger}`}
                              title={
                                user.isGranted
                                  ? "Revoke their CMS access (their account is kept)"
                                  : "Delete this user"
                              }
                              onClick={() => setConfirm({ type: "delete", user })}
                            >
                              <IoTrashOutline />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setPage}
            />
          </>
        )}
      </div>

      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editingUser={editingUser}
        roles={roles}
        onSaved={load}
      />

      {/* For a granted Creator/Subscriber this revokes CMS access and keeps
          the account, so promising a permanent delete would be both alarming
          and untrue. The backend makes the same distinction. */}
      <ConfirmDialog
        open={confirm?.type === "delete"}
        danger
        title={confirm?.user?.isGranted ? "Revoke CMS access?" : "Delete user?"}
        message={
          confirm?.user?.isGranted
            ? `${confirm?.user?.name} will lose access to the CMS. Their ${
                confirm?.user?.accountType === "creator" ? "creator" : "subscriber"
              } account stays exactly as it is.`
            : `This will permanently remove ${confirm?.user?.name}. This action cannot be undone.`
        }
        confirmLabel={confirm?.user?.isGranted ? "Revoke access" : "Delete user"}
        loading={confirmLoading}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm?.type === "reset"}
        title="Reset password?"
        // For a granted row this resets the password on their EXISTING
        // website account — the one they use to watch or upload — not some
        // separate CMS credential. Everything else in this screen is careful
        // to keep the two apart, so say so here rather than letting an
        // operator assume otherwise.
        message={
          confirm?.user?.isGranted
            ? `Send a password reset to ${confirm?.user?.email}? This resets the password for their ${
                confirm?.user?.accountType === "creator" ? "creator" : "subscriber"
              } account, which they also use to sign in to the website.`
            : `Send a password reset to ${confirm?.user?.email}?`
        }
        confirmLabel="Send reset"
        loading={confirmLoading}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default UsersList;
